import React, { useEffect } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import Thread from "../../../../components/social/Thread";
import flagService from "../../../../services/flagService";
import store from "../../../../state/store";
import isPortal from "../../../../common/helpers/isPortal";
import withErrorGuard from "../../../../HOC/withErrorGuard";
import SourceEntity from "../../../../components/Publications/models/SourceEntity";
import ClassificationModal from "../ClassificationModal/ClassificationModal";
import StyledComponentPanel from "../../../../components/atoms/ComponentPanel/StyledComponentPanel";
import { getPubmedIdsForUserEntries } from "../../../../state/selectors/PubmedUserEntries.selectors";
import { getSelectedUserViewClinical } from "../../../../state/selectors/CardsUiClinicalSelectors";
import PubmedForm from "../../../../components/PubmedUserEntries/PubmedForm";
import FlagContainer from "../../../../components/social/FlagContainer";
import {
  getVariantFlagContributions,
  getUserFlagSelector,
  getQueryType,
} from "../../../../state/selectors/CommunityContributions.selectors";
import {
  getSearchEntity,
  getCommentsCount,
} from "../../../../state/selectors/Publications.selectors";
import PublicationLinks from "../../../../components/Publications/components/PublicationLinks";

function CommunityContributions({
  queryType,
  commentsCount,
  userFlag,
  searchEntity,
  linkedPubmedIds,
  flags,
}) {
  const isClinical = isPortal();
  const currentFlag = Object.keys(userFlag || {}).length > 0 ? userFlag : null;
  const linkLen = linkedPubmedIds.length;
  const isGene = queryType === "gene";
  const flagLen = flags?.length || 0;
  const totalLen = isGene ? commentsCount : flagLen + commentsCount + linkLen;
  const commentSlug = searchEntity.getSlugForComments();
  const titleCl = "tw-font-bold tw-text-blueGray-500 tw-flex-grow tw-py-3";

  useEffect(() => {
    if (isClinical) flagService.register(store);
  }, []);

  return (
    <StyledComponentPanel
      title={
        isGene ? (
          <div className="tw-font-bold">
            Community Contributions ({totalLen})
          </div>
        ) : (
          <div className="tw-flex tw-items-center tw-w-full tw-gap-6 tw-text-xs tw-font-bold tw-flex-grow">
            <div className="tw-text-sm">
              <div>Community Contributions ({totalLen})</div>
            </div>
            <div className="tw-flex-grow" />
            {isClinical ? null : (
              <div className="tw-text-3xs tw--my-2">
                <ClassificationModal userFlag={currentFlag} />
              </div>
            )}
            {isClinical ? null : (
              <div className="tw-text-3xs tw--my-2">
                <PubmedForm />
              </div>
            )}
          </div>
        )
      }
    >
      <div className="tw-px-4">
        <div className="tw-text-sm">
          {isClinical || isGene ? null : (
            <div className="tw-pb-16 tw-border-t tw-border-blueGray-200">
              <div className="tw-flex tw-items-center">
                <div className={titleCl}>
                  Linked Publications for {searchEntity?.description}
                </div>
              </div>

              <PublicationLinks
                forceList={true}
                shouldShowContributions={true}
                sourceEntity={searchEntity}
                ids={linkedPubmedIds}
                isModal={false}
              />
            </div>
          )}

          {isGene ? null : (
            <div className="tw-pb-16 tw-border-t tw-border-blueGray-200">
              <div className="tw-flex tw-items-center">
                <div className={titleCl}>
                  Classifications for {searchEntity.description}
                </div>
              </div>

              {flags.map((x) => (
                <FlagContainer
                  key={x.id}
                  contribution={x}
                  sourceEntity={searchEntity}
                  userFlag={userFlag}
                />
              ))}
            </div>
          )}

          <div className="tw-pb-16 tw-border-t tw-border-blueGray-200">
            <div className="tw-flex tw-items-center">
              <div className={titleCl}>
                User Comments for {searchEntity.description}
              </div>
            </div>

            <Thread
              sourceEntity={searchEntity}
              subType={null}
              subTypeId={null}
              slug={commentSlug}
              shouldAutoExpand={true}
              shouldHideExpandButton={true}
              shouldHideLines={true}
            />
          </div>
        </div>
      </div>
    </StyledComponentPanel>
  );
}

CommunityContributions.propTypes = {
  commentsCount: PropTypes.number,
  searchEntity: PropTypes.instanceOf(SourceEntity),
  userFlag: PropTypes.shape({}),
  queryType: PropTypes.string,
  linkedPubmedIds: PropTypes.arrayOf(PropTypes.string),
  flags: PropTypes.arrayOf(PropTypes.shape({})),
};

CommunityContributions.defaultProps = {
  commentsCount: 0,
  searchEntity: null,
  userFlag: {},
  queryType: null,
  linkedPubmedIds: [],
  flags: [],
};

const mapStateToProps = (state) => {
  return {
    commentsCount: getCommentsCount(state),
    searchEntity: getSearchEntity(state),
    userFlag: getUserFlagSelector(state),
    linkedPubmedIds: getPubmedIdsForUserEntries(state),
    commentLen: getCommentsCount(state),
    flags: getVariantFlagContributions(state),
    queryType: isPortal()
      ? getSelectedUserViewClinical(state)
      : getQueryType(state),
  };
};

const guarded = withErrorGuard(
  React.memo(CommunityContributions),
  "CommunityContributions",
  "Community Contributions"
);

export default connect(mapStateToProps, {})(guarded);
